<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { usePageStore } from '@toni/rendering/src/stores/page'
import { useSessionStore } from '@toni/rendering/src/stores/session'
import { Embed, classMerge, BlockContainer, PrivacyShield } from '@toni/brand-ui'

import { useContentBlock } from '../../../content-blocks/composables/use-content-block'
import ConsentWrapper from '../../consent/ConsentWrapper.vue'
import { useClient } from '../../../utils/client-only'
import EmbedContentFacebook from './EmbedContentFacebook.vue'
import EmbedContentInstagram from './EmbedContentInstagram.vue'
import EmbedContentTiktok from './EmbedContentTiktok.vue'
import EmbedContentTwitter from './EmbedContentTwitter.vue'
import EmbedContentSpotify from './EmbedContentSpotify.vue'
import EmbedContentYoutube from './EmbedContentYoutube.vue'
import EmbedContentVimeo from './EmbedContentVimeo.vue'

const props = defineProps<{ id: string }>()

const id = computed(() => props.id)
const contentBlock = useContentBlock(id.value, 'embed')

const embedComponents: Record<string, typeof EmbedContentFacebook | typeof EmbedContentInstagram | typeof EmbedContentTwitter | typeof EmbedContentSpotify | typeof EmbedContentYoutube | typeof EmbedContentVimeo> = {
  facebook: EmbedContentFacebook,
  twitter: EmbedContentTwitter,
  instagram: EmbedContentInstagram,
  tiktok: EmbedContentTiktok,
  spotify: EmbedContentSpotify,
  youtube: EmbedContentYoutube,
  vimeo: EmbedContentVimeo
}

const currentEmbedComponent = computed(() => {
  return embedComponents[contentBlock.value.data.embed.embedType] || null
})

const { isClient } = useClient()
const { t } = useI18n()

const pageStore = usePageStore()
const sessionStore = useSessionStore()
const newLayoutTypes = ['instagram', 'facebook', 'twitter', 'tiktok', 'spotify', 'youtube', 'vimeo', 'embed']

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

const useNewLayout = computed(() => newLayoutTypes.includes(contentBlock.value.data.embed.embedType))
const isHome = computed(() => pageStore.isHome as boolean)
const hasPurSubOrContentPass = computed(() => sessionStore.hasPurSubOrContentPass)
const size = computed(() => isHome.value ? 'full' : 'normal')

const headline = computed(() => {
  return hasPurSubOrContentPass.value
    ? t('privacyShield.headline.withPurSub')
    : t('privacyShield.headline.withoutPurSub')
})

const description = computed(() => {
  if (hasPurSubOrContentPass.value) {
    return t('privacyShield.description.withPurSub')
  }

  if (useNewLayout.value) {
    return [
      t('privacyShield.newLayout.description.consent', { embedType: capitalize(contentBlock.value.data.embed.embedType) }),
      t('privacyShield.newLayout.description.withdrawal'),
      t('privacyShield.newLayout.description.validity'),
      t('privacyShield.newLayout.description.agreement')
    ]
  }

  return [
    t('privacyShield.description.withoutPurSub.consent', { embedType: contentBlock.value.data.embed.embedType }),
    t('privacyShield.description.withoutPurSub.withdrawal'),
    t('privacyShield.description.withoutPurSub.validity'),
    t('privacyShield.description.withoutPurSub.agreement')
  ]
})

</script>

<template>
  <BlockContainer v-if="isClient"
                  data-testid="embed-container"
                  :size="size"
                  :class="classMerge({
                    'items-center': ['facebook', 'instagram', 'twitter'].includes(contentBlock.data.embed.embedType),
                    'mt-3 md:mt-4 mb-6 md:mb-8': contentBlock.data.embed.embedType !== 'youtube' || 'vimeo',
                    'relative pb-[56.25%] md:pb-[36%] lg:w-[764px] lg:min-h-[429px] lg:pb-0 mb-8': contentBlock.data.embed.embedType === 'vimeo',
                  })">
    <ConsentWrapper :vendor="contentBlock.data.embed.embedType"
                    :has-pur-sub="hasPurSubOrContentPass">
      <template #enabled>
        <div :class="{
          'relative pb-[56.25%] h-0': contentBlock.data.embed.embedType === 'youtube',
          'min-w-[220px]': contentBlock.data.embed.embedType === 'facebook'
        }">
          <Embed>
            <component :is="currentEmbedComponent"
                       :html="contentBlock.data.embed.raw ?? ''"
                       :style="contentBlock.data.embed.embedType === 'tiktok' ? { height: '420px' } : {}" />
          </Embed>
        </div>
      </template>
      <template #disabled="{ onConsentClick }">
        <div class="relative">
          <PrivacyShield
            :new-layout="useNewLayout"
            :embed-type="contentBlock.data.embed.embedType"
            :has-pur-sub="hasPurSubOrContentPass"
            :headline="headline"
            :description="description"
            :consent-button-text="useNewLayout ? t('privacyShield.newLayout.consent') : t('privacyShield.buttons.embed.consent')"
            :privacy-policy-button-text="t('privacyShield.buttons.privacyPolicy')"
            @consent-click="onConsentClick" />
        </div>
      </template>
    </ConsentWrapper>
  </BlockContainer>
</template>
